// Utility for shifting hardcoded/stale dates in the static JSON "mock backend"
// fixtures so they stay relevant relative to "today", without editing the
// raw JSON files (which would just freeze a new stale date).
//
// Approach: for a given dataset + list of date field names, find the max
// (most recent) date across those fields, compute how many days that date
// is from "today", then shift EVERY date in EVERY record by that same
// offset. This preserves the relative spacing between all dates in the
// dataset while moving the whole range to end on/near today.
//
// Only the date portion (YYYY-MM-DD) of each string is modified — the time,
// fractional seconds, and timezone suffix (if present) are left untouched,
// so formats like "2024-06-29T00:00:00+02:00" or
// "2024-06-29T21:02:53.1314971" round-trip exactly as they were, just on a
// different day.

const DATE_PREFIX_REGEX = /^(\d{4})-(\d{2})-(\d{2})(.*)$/;

function parseDatePart(dateStr) {
  if (typeof dateStr !== "string") return null;
  const match = dateStr.match(DATE_PREFIX_REGEX);
  if (!match) return null;
  const [, y, m, d] = match;
  return Date.UTC(Number(y), Number(m) - 1, Number(d));
}

function shiftDateString(dateStr, days) {
  if (typeof dateStr !== "string") return dateStr;
  const match = dateStr.match(DATE_PREFIX_REGEX);
  if (!match) return dateStr;
  const [, y, m, d, rest] = match;
  const dt = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)));
  dt.setUTCDate(dt.getUTCDate() + days);
  const newY = dt.getUTCFullYear();
  const newM = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const newD = String(dt.getUTCDate()).padStart(2, "0");
  return `${newY}-${newM}-${newD}${rest}`;
}

function findMaxDateTime(records, fields) {
  let max = -Infinity;
  records.forEach((record) => {
    fields.forEach((field) => {
      const t = parseDatePart(record[field]);
      if (t !== null && t > max) max = t;
    });
  });
  return max;
}

function computeShiftDays(maxDateTime, referenceDate) {
  if (maxDateTime === -Infinity) return 0;
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  const todayUTC = Date.UTC(
    referenceDate.getFullYear(),
    referenceDate.getMonth(),
    referenceDate.getDate()
  );
  return Math.round((todayUTC - maxDateTime) / MS_PER_DAY);
}

/**
 * Shifts the given date fields on every record of an array so that the
 * latest date across all of them lands on `referenceDate` (defaults to
 * today), preserving the relative spacing between all dates.
 *
 * @param {Array<Object>} records
 * @param {string[]} fields - names of date fields (e.g. ["dataRegjistrimit"])
 * @param {Date} [referenceDate]
 * @returns {Array<Object>} new array with shifted date fields
 */
export function shiftRecordDates(records, fields, referenceDate = new Date()) {
  if (!Array.isArray(records)) return records;
  const maxDateTime = findMaxDateTime(records, fields);
  const shiftDays = computeShiftDays(maxDateTime, referenceDate);
  if (shiftDays === 0) return records;
  return records.map((record) => {
    const shifted = { ...record };
    fields.forEach((field) => {
      shifted[field] = shiftDateString(record[field], shiftDays);
    });
    return shifted;
  });
}

/**
 * Same as shiftRecordDates, but for a JSON shape that wraps the array of
 * records inside an object (e.g. { totaletDitore: [...], totaletJavore: {} }).
 *
 * @param {Object} obj
 * @param {string} arrayKey - key on `obj` holding the array of records
 * @param {string[]} fields
 * @param {Date} [referenceDate]
 * @returns {Object} new object with the nested array's dates shifted
 */
export function shiftNestedRecordDates(
  obj,
  arrayKey,
  fields,
  referenceDate = new Date()
) {
  if (!obj || !Array.isArray(obj[arrayKey])) return obj;
  return {
    ...obj,
    [arrayKey]: shiftRecordDates(obj[arrayKey], fields, referenceDate),
  };
}
